import Signature from "../../../components/signature/Signature";
import TotalNumberCard from "../../../components/cards/TotalNumberCard";
import { Chart as ChartJS, CategoryScale } from "chart.js/auto";
import { ClipboardText, Profile2User } from "iconsax-react";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";
import { useLoaderData, useNavigate } from "react-router-dom";
import { Pie, Line } from "react-chartjs-2";
import {
  NewOrder,
  RefillOrder,
} from "../../../components/order_types/order_types";
import { CheckCheck } from "lucide-react";

export const overviewLoader = async () => {
  const token = getToken();
  const data = await instance.get("/doctor/data/medication", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const stats = await instance.get("/doctor/overview/stats", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const analytics = await instance.get("/doctor/overview/analytics", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const orders = await instance.get(`/doctor/orders?page_index=0&page_size=5`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    stats: stats.data,
    analytics: analytics.data,
    mostOrdered: data.data,
    orders: orders.data?.orders || [],
  };
};

ChartJS.register(CategoryScale);

export default function OverviewPage() {
  const { stats, analytics, mostOrdered, orders } = useLoaderData();
  const navigate = useNavigate();

  const chartData = {
    labels: mostOrdered.map((data) => data.name),
    datasets: [
      {
        label: "No of Orders",
        data: mostOrdered.map((data) => data.count),
        backgroundColor: ["#50AF95", "#f3ba2f", "#2a71d0"],
        borderColor: "#ccc",
        borderWidth: 1,
      },
    ],
  };

  const lineChartData = {
    labels: analytics.map((day) => day.date),
    datasets: [
      {
        label: "Shipped Orders",
        data: analytics.map((day) => day.orders.shipped),
        borderColor: "#3caf50",
        borderWidth: 2,
        backgroundColor: ["#3caf50"],
      },
      {
        label: "Accepted Orders",
        data: analytics.map((day) => day.orders.accepted),
        borderColor: "tomato",
        borderWidth: 2,
        backgroundColor: ["tomato"],
      },
      {
        label: "Placed Orders",
        data: analytics.map((day) => day.orders.placed),
        borderColor: "#2a71d0",
        borderWidth: 2,
        backgroundColor: ["#2a71d0"],
      },
    ],
  };

  return (
    <div className="bg-[#fafafa] px-7 py-5 max-[100vh] overflow-y-scroll">
      <div className="text-2xl font-bold">Overview</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 [&>*]:bg-white [&>*]:border mt-6 grid-cols-1">
        <TotalNumberCard
          title="Total Patients"
          value={stats.patients}
          color="dodgerblue"
          icon={<Profile2User variant="Bold" size={20} />}
          href="/dashboard/patients"
        />

        <TotalNumberCard
          title="Facility Members"
          value={stats.members}
          color="dodgerblue"
          icon={<Profile2User variant="Bold" size={20} />}
          href="/dashboard/facility/members"
        />

        <TotalNumberCard
          title="Total Orders"
          value={stats.orders}
          color="dodgerblue"
          icon={<ClipboardText variant="Bold" size={20} />}
          href="/dashboard/orders"
        />
      </div>
      <div className="flex justify-between flex-col xl:flex-row mt-10 gap-x-10 gap-y-10">
        <div className="bg-white shadow-md py-5 px-5 rounded-2xl w-full">
          <LineChart chartData={lineChartData} />
        </div>
        <div className="bg-white shadow-md py-5 px-5 rounded-2xl">
          <div className="max-w-[400px] lg:max-w-[400px] 2xl:max-w-none mx-auto">
            <PieChart chartData={chartData} />
          </div>
        </div>
      </div>
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td className="px-6 py-4">#</td>
                <td className="px-6 py-4">Order ID</td>
                <td className="px-6 py-4">Type</td>
                <td className="px-6 py-4">Shipping service</td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="intake">
                  I
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="ppv1">
                  PPV1
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="pv1">
                  PV1
                </td>
                <td
                  className="w-[40px] px-1 py-2"
                  data-tooltip-id="fulfillment"
                >
                  F
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="pv2">
                  PV2
                </td>
                <td className="w-[40px] px-1 py-2" data-tooltip-id="shipping">
                  S
                </td>
                <td className="px-6 py-4">Payment Status</td>
                <td className="px-6 py-4">Date</td>
                <td></td>
              </tr>
            </thead>

            <tbody>
              {orders.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No recent orders
                  </td>
                </tr>
              ) : (
                orders.map((order, index) => (
                  <tr key={order.id} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-4">{index + 1}</td>
                    <td className="px-6 py-4">{order.id}</td>
                    <td className="px-6 py-4">
                      {order.order_type === "Refill" ? (
                        <RefillOrder />
                      ) : (
                        <NewOrder />
                      )}
                    </td>
                    <td className="px-6 py-4 capitalize">
                      {order.shipping_service
                        ? JSON.parse(order.shipping_service).name
                        : "--"}
                    </td>
                    {order.steps.map((step) => (
                      <td
                        className="px-1 py-2 capitalize w-[40px]"
                        key={step.step}
                      >
                        {step.is_done ? (
                          <CheckCheck size={16} color="green" />
                        ) : (
                          "-"
                        )}
                      </td>
                    ))}
                    <td className="px-6 py-4 capitalize">
                      {order.payment_status}
                    </td>
                    <td className="px-6 py-4">
                      {
                        new Date(order.created_at)
                          .toLocaleString()
                          .split(",")[0]
                      }
                    </td>
                    <td
                      onClick={() => navigate(`/dashboard/orders/${order.id}`)}
                      className="px-6 py-4 underline text-primary cursor-pointer"
                    >
                      View order
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PieChart({ chartData }) {
  return (
    <div className="">
      <Pie
        data={chartData}
        options={{
          plugins: {
            title: {
              display: true,
              text: "3 Most Frequest Ordered Drugs",
            },
          },
        }}
      />
    </div>
  );
}

function LineChart({ chartData }) {
  return (
    <div className="chart-container">
      <Line
        data={chartData}
        options={{
          plugins: {
            title: {
              display: true,
              text: "Order Status",
            },
            legend: {
              display: true,
            },
          },
          scales: {
            x: {
              beginAtZero: true,
            },
            y: {
              beginAtZero: true,
            },
          },
        }}
      />
    </div>
  );
}
